<script setup lang="ts">
import { ArrowSmDownIcon, ArrowSmRightIcon, RefreshIcon } from '@heroicons/vue/solid';
import type { PropType } from 'vue';
import type { CreateOfferParams } from '~/composables/useGoby';

const props = defineProps({
  nft: { type: Object as PropType<any>, required: true },
  side: { type: Number as PropType<1 | 2>, default: () => 1 },
});
const nft = toRef(props, 'nft');
const side = ref(props.side);

const emit = defineEmits(['createdOffer']);

const gobyStore = useGobyStore();
const showConnectWalletModal = useState('showConnectWalletModal', () => false);

const toggleSide = () => {
  side.value = side.value === 1 ? 2 : 1;
};
const price = ref<number | undefined>(undefined);

const waitingForConfirmation = ref(false);

const createOfferSuccess = ref(false);
const createOfferFailed = ref(false);
const createOfferError = ref<string | undefined>(undefined);

const createOfferWithGoby = async () => {
  if (!gobyStore.account) {
    showConnectWalletModal.value = true;
    return;
  }
  createOfferError.value = undefined;
  try {
    const params: CreateOfferParams = {
      requestAssets: [],
      offerAssets: [],
    };

    const nftEntry = { amount: 1, assetId: nft.value.encoded_id };
    if (side.value === 1) {
      params.offerAssets.push(nftEntry);
      if (price.value && price.value > 0) {
        params.requestAssets.push({ amount: price.value * 10 ** 12, assetId: '' });
      } else if (!confirm('Do you really want to offer this NFT for free?')) {
        return;
      }
    } else {
      params.requestAssets.push(nftEntry);
      if (price.value && price.value > 0) {
        params.offerAssets.push({ amount: price.value * 10 ** 12, assetId: '' });
      }
    }

    try {
      const offer = await gobyStore.createOffer(params);
      emit('createdOffer', offer.offer);
      createOfferSuccess.value = true;
    } catch (e) {
      console.log(e);
      if (e.message === 'nft coin is loced') {
        createOfferError.value = 'This NFT is locked in your wallet.';
      } else {
        createOfferError.value = e.message || 'Request Rejected';
      }
      createOfferFailed.value = true;
    }
  } catch (e) {
    createOfferFailed.value = true;
  } finally {
    waitingForConfirmation.value = false;
  }
};
</script>
<template>
  <div class="my-8 grid gap-4 px-4 lg:my-16 lg:grid-cols-[1fr_64px_1fr] lg:grid-rows-[auto_1fr] lg:gap-x-12">
    <div class="row-end-1 text-2xl">{{ $t('offers.offering') }}</div>
    <div class="row-end-1 hidden lg:block" />
    <div class="flex flex-col">
      <div v-if="side === 2" class="flex flex-grow flex-col items-center justify-center gap-2">
        <div class="relative mt-2 w-full rounded-full shadow-sm">
          <input
            v-model="price"
            type="number"
            name="price"
            class="block w-full rounded-full border-0 py-3 pl-6 pr-14 text-lg text-neutral-900 ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-black dark:bg-neutral-800 dark:text-neutral-50 dark:text-neutral-500 dark:text-white dark:ring-neutral-600 sm:leading-6"
            placeholder="0.00"
            min="0"
          />
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span class="text-lg text-neutral-500 dark:text-neutral-400"> XCH </span>
          </div>
        </div>
        <!--        <span class="text-xs text-neutral-600 dark:text-neutral-300"-->
        <!--          >Including 2.5% marketplace fee<span v-if="nft.royalty_percentage">-->
        <!--            and {{ nft.royalty_percentage / 100 }}% creator royalties</span-->
        <!--          ></span-->
        <!--        >-->
        <span v-if="nft.royalty_percentage" class="text-xs text-neutral-600 dark:text-neutral-300"
          >Plus {{ nft.royalty_percentage / 100 }}% creator royalties</span
        >

        <div class="mt-2 flex w-full">
          <button
            v-if="gobyStore.isWalletInstalled"
            class="text-md betterhover:hover:shadow-card-hover flex w-full justify-center rounded-full border border-transparent bg-green-600 px-4 py-2 font-medium text-white transition duration-100 ease-in-out betterhover:hover:-translate-y-0.5"
            @click="createOfferWithGoby"
          >
            <img
              v-if="gobyStore.type === 'goby'"
              :class="['-ml-1 mr-2 h-6 w-6']"
              aria-hidden="true"
              src="/goby-logo-white.svg"
            />
            <img
              v-else-if="gobyStore.type === 'walletconnect'"
              :class="['-ml-1 mr-2 h-6 w-6']"
              aria-hidden="true"
              src="https://sagewallet.net/assets/sage-logo.png"
            />
            {{ gobyStore.account ? 'Create offer' : 'Connect' }}
          </button>
        </div>
      </div>
      <div v-else>
        <NftCard :nft="nft" />
      </div>
    </div>
    <button class="group flex flex-grow flex-col items-center justify-center" @click="toggleSide()">
      <div>
        <ArrowSmRightIcon class="hidden h-12 w-12 text-neutral-300 group-hover:hidden dark:text-neutral-600 lg:block" />
        <RefreshIcon class="hidden h-12 w-12 text-neutral-300 dark:text-neutral-600 group-hover:lg:block" />
        <ArrowSmDownIcon class="h-12 w-12 text-neutral-300 dark:text-neutral-600 lg:hidden" />
      </div>
      <div class="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Flip</div>
    </button>
    <div class="text-2xl lg:row-end-1">{{ $t('offers.requesting') }}</div>
    <div class="flex flex-col">
      <div v-if="side === 1" class="flex flex-grow flex-col items-center justify-center gap-2">
        <div class="relative mt-2 w-full rounded-full shadow-sm">
          <input
            v-model="price"
            type="number"
            name="price"
            class="block w-full rounded-full border-0 py-3 pl-6 pr-14 text-lg text-neutral-900 ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-black dark:bg-neutral-800 dark:ring-neutral-600 sm:leading-6"
            placeholder="0.00"
            step="any"
            min="0"
          />
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span class="text-lg text-neutral-500 dark:text-neutral-400"> XCH </span>
          </div>
        </div>
        <!--        <span class="text-xs text-neutral-600 dark:text-neutral-300"-->
        <!--          >Including 2.5% marketplace fee<span v-if="nft.royalty_percentage">-->
        <!--            and {{ nft.royalty_percentage / 100 }}% creator royalties</span-->
        <!--          ></span-->
        <!--        >-->
        <span v-if="nft.royalty_percentage" class="text-xs text-neutral-600 dark:text-neutral-300">
          Plus {{ nft.royalty_percentage / 100 }}% {{ $t('offers.creator_royalties') }}
        </span>

        <div class="mt-2 flex w-full">
          <button
            v-if="gobyStore.isWalletInstalled"
            class="text-md betterhover:hover:shadow-card-hover flex w-full justify-center rounded-full border border-transparent bg-green-600 px-4 py-2 font-medium text-white transition duration-100 ease-in-out betterhover:hover:-translate-y-0.5"
            @click="createOfferWithGoby"
          >
            <img
              v-if="gobyStore.type === 'goby'"
              :class="['-ml-1 mr-2 h-6 w-6']"
              aria-hidden="true"
              src="/goby-logo-white.svg"
            />
            <img
              v-else-if="gobyStore.type === 'walletconnect'"
              :class="['-ml-1 mr-2 h-6 w-6']"
              aria-hidden="true"
              src="https://sagewallet.net/assets/sage-logo.png"
            />
            {{ gobyStore.account ? 'Create offer' : 'Connect' }}
          </button>
        </div>
      </div>
      <div v-else>
        <NftCard :nft="nft" />
      </div>
    </div>
    <div v-if="createOfferError" class="col-span-3 mt-4 flex justify-center">
      <div class="text-sm font-medium text-red-600">{{ createOfferError }}</div>
    </div>
  </div>
</template>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
